import { useQuery } from "@tanstack/react-query";
import { getUserProfile } from "../api/auth";
import { useAuth } from "../context/AuthContext";
import type { UserProfile } from "../types/auth";

export function useUserProfile(userId?: string | null) {
  return useQuery({
    queryKey: ["user-profile", userId],
    queryFn: async (): Promise<UserProfile> => {
      if (!userId) throw new Error("userId is required");
      const { user, can_submit } = await getUserProfile(userId);
      // The backend puts `can_submit` on the response wrapper, not on
      // `user` (see UserProfileResponse) — merge it onto the flattened
      // profile so callers can read `profile.can_submit` directly, same
      // shape as every other profile field. `null` (backend's explicit
      // "not computed") collapses to `undefined` so callers only ever
      // see `true`, `false`, or absent.
      return { ...user, can_submit: can_submit ?? undefined };
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useCurrentUserProfile() {
  const { auth } = useAuth();
  return useUserProfile(auth?.user.id);
}
